import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SAMPLE_WORKFLOWS, SAMPLE_EXECUTIONS } from '../data/sampleWorkflows';

export default function Dashboard() {
  const [workflows, setWorkflows] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Load sample data
    setWorkflows(SAMPLE_WORKFLOWS);
    setExecutions(SAMPLE_EXECUTIONS);
    setLoading(false);
  }, []);

  const activeWorkflows = workflows.filter(w => w.status === 'published').length;
  const totalExecutions = workflows.reduce((sum, w) => sum + w.executions, 0);

  return (
    <div className="flex-1">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h2>
          <p className="text-gray-600">Manage and monitor your workflows</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="card p-6">
            <p className="text-gray-600 text-sm font-medium">Total Workflows</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{workflows.length}</p>
          </div>
          <div className="card p-6">
            <p className="text-gray-600 text-sm font-medium">Published</p>
            <p className="text-3xl font-bold text-green-600 mt-2">{activeWorkflows}</p>
          </div>
          <div className="card p-6">
            <p className="text-gray-600 text-sm font-medium">Total Executions</p>
            <p className="text-3xl font-bold text-blue-600 mt-2">{totalExecutions}</p>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Workflows List */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Workflows</h3>
              <button onClick={() => navigate('/builder')} className="btn btn-primary text-sm">
                + New Workflow
              </button>
            </div>

            {loading ? (
              <p className="text-gray-500">Loading workflows...</p>
            ) : workflows.length === 0 ? (
              <p className="text-gray-500 py-8 text-center">No workflows yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {workflows.map((workflow) => (
                  <div key={workflow.id} className="workflow-item">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{workflow.displayName}</h4>
                          <span className={`badge ${workflow.status === 'published' ? 'badge-success' : 'badge-warning'}`}>
                            {workflow.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{workflow.description}</p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span>📍 {workflow.steps} steps</span>
                          <span>▶️ {workflow.executions} runs</span>
                          <span>⏱️ {workflow.lastRun}</span>
                        </div>
                      </div>
                      <button className="btn btn-primary text-xs">Run</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Executions */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Executions</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {executions.map((exec) => (
                <div key={exec.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-gray-900">{exec.workflowName}</p>
                      <div className="flex gap-3 mt-2 text-xs text-gray-600">
                        <span>{exec.startedAt.split('T')[1].slice(0, 5)}</span>
                        <span>⏱️ {exec.duration}</span>
                        <span>📊 {exec.steps} steps</span>
                      </div>
                    </div>
                    <span className={`badge ${exec.status === 'success' ? 'badge-success' : 'badge-error'}`}>
                      {exec.status === 'success' ? '✓' : '✗'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
